import { DefaultOptionType } from 'rc-cascader/lib/Cascader';
import { Key } from 'antd/lib/table/interface';

import { IFilter } from 'components/Filters/constants';
import { EEditType } from 'types/commonEnums';
import { IOrder } from 'types/ordersTypes';
import { IProduct } from 'types/productsTypes';
import { ICleanStoreAction } from 'store/reducers/types';

export enum EListsTypes {
  OrdersType = 'orders',
  ProductsType = 'products',
}

export enum EProductConditions {
  New = 'new',
  Used = 'used'
}

export interface IListsPagination {
  isNeedUpdate: boolean,
  isLoading: boolean,
  page: number,
  offset: number,
  limit: number,
  totalResults: number,
  condition: string,
  orderBy: any,
  isSelectedAll: boolean,
  syncStatus: 'syncing' | 'finished' | null,
}

export interface ISavedFilters {
  isUpdated: boolean,
  filters: any[],
}

export interface IListsTags {
  isUpdated: boolean,
  tagsList: string[]
}

export interface IShopifyCollections {
  id: string,
  title: string
}

export interface ICustomizeTableColumn {
  title: string,
  dataIndex: string,
  isFixed?: boolean,
  isChecked: boolean,
}

interface IDefaultList {
  pagination: IListsPagination,
  activeFilters: IFilter[],
  savedFilters: ISavedFilters,
  tags: IListsTags
  tableSettings: ICustomizeTableColumn[]
}

interface IProductsList extends IDefaultList {
  results: IProduct[];
  shopifyCollections: {
    isUpdated: boolean,
    list: IShopifyCollections[],
  };
  isBulkActivating?: boolean,
}

interface IOrdersList extends IDefaultList {
  results: IOrder[],
}

export enum EListsActions {
  GetListResultsByType = 'GET_LIST_RESULTS_BY_TYPE',
  SetPaginationByType = 'SET_PRODUCTS_PAGINATION',
  SetActiveFiltersByType = 'SET_ACTIVE_FILTERS_BY_TYPE',
  SaveFiltersByType = 'SAVE_FILTERS_BY_TYPE',
  UpdateTableSettingsByType = 'UPDATE_TABLE_SETTINGS_BY_TYPE',
  UpdateProduct = 'UPDATE_PRODUCT',
  BulkUpdateProducts = 'BULK_UPDATE_PRODUCTS',
  GetProductsTags = 'GET_PRODUCTS_TAGS',
  GetProductsShopifyCollections = 'GET_PRODUCTS_SHOPIFY_COLLECTIONS',
  IsBulkActivating = 'IS_BULK_ACTIVATING',
}

export interface IListsReducer {
  [EListsTypes.ProductsType]: IProductsList,
  [EListsTypes.OrdersType]: IOrdersList,
}

export interface IGetResultsByTypeActionProps {
  listType: EListsTypes,
}

export interface IGetResultsByTypeAction {
  type: EListsActions.GetListResultsByType;
  payload: {
    listType: EListsTypes
    results: IProduct[] | IOrder[];
    pagination: IListsPagination;
  };
}

export interface ISetPaginationByTypeActionProps {
  listType: EListsTypes,
  pagination: Partial<IListsPagination>,
}

export interface ISetPaginationByTypeAction {
  type: EListsActions.SetPaginationByType,
  payload: {
    listType: EListsTypes,
    pagination: IListsPagination,
  }
}

export interface ISetActiveFiltersByTypeActionProps {
  listType: EListsTypes,
  activeFilters: Partial<IFilter>[]
}

export interface IUpdateTableSettingsByTypeActionProps {
  listType: EListsTypes,
  tableSettings: ICustomizeTableColumn[],
}

export interface IUpdateTableSettingsByTypeAction {
  type: EListsActions.UpdateTableSettingsByType,
  payload: IUpdateTableSettingsByTypeActionProps,
}

export interface ISetActiveFiltersByTypeAction {
  type: EListsActions.SetActiveFiltersByType;
  payload: {
    listType: EListsTypes,
    activeFilters: IFilter[] | [],
  };
}

export interface IGetProductsTagsAction {
  type: EListsActions.GetProductsTags,
  payload: { isUpdated: boolean, tagsList: string[] }
}

export interface IGetProductsShopifyCollectionsAction {
  type: EListsActions.GetProductsShopifyCollections,
  payload: {
    isUpdated: boolean,
    list: IShopifyCollections[]
  }
}

export interface IUpdateProductAction {
  type: EListsActions.UpdateProduct;
  payload: any;
}

export interface IBulkUpdateProductsActionProps {
  ids: Key[],
  type: EEditType,
  isActive?: boolean,
  tags?: string[]
  categoryId?: DefaultOptionType['value'];
  categoryName?: DefaultOptionType['label'];
  categories?: DefaultOptionType['value'][];
  productCondition?: EProductConditions,
  isBulkActivate?: boolean,
}

export interface IBulkUpdateProductsAction {
  type: EListsActions.BulkUpdateProducts;
  payload: any;
}

export interface ISaveFiltersByTypeAction {
  type: EListsActions.SaveFiltersByType;
  payload: { listType: EListsTypes, savedFilters: any };
}

export interface IIsBulkActivatingAction {
  type: EListsActions.IsBulkActivating;
  payload: { isBulkActivating: boolean };
}

export type TListsActions =
  IGetResultsByTypeAction
  | ISetPaginationByTypeAction
  | ISetActiveFiltersByTypeAction
  | IUpdateTableSettingsByTypeAction
  | IGetProductsTagsAction
  | IUpdateProductAction
  | IBulkUpdateProductsAction
  | ISaveFiltersByTypeAction
  | IGetProductsShopifyCollectionsAction
  | IIsBulkActivatingAction
  | ICleanStoreAction
